import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { Button } from '../components/ui/button';
import { 
  ArrowLeft, ArrowRight, CheckCircle2, Sparkles, Star, Package,
  Globe, Printer, Megaphone, Camera, Zap, Clock, Shield, Gift
} from 'lucide-react';
import { usePageMeta } from '../hooks/usePageMeta';

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15, delayChildren: 0.2 }
  }
};

// Bundle Card Component
const BundleCard = ({ bundle, featured, index }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      whileHover={{ y: -8 }}
      className={`relative rounded-3xl overflow-hidden h-full ${
        featured 
          ? 'bg-gradient-to-br from-[#1E3A8A] to-[#3B82F6] text-white shadow-2xl ring-4 ring-amber-400/50' 
          : 'bg-white border-2 border-gray-100 shadow-xl hover:shadow-2xl hover:border-[#1E3A8A]/20'
      } transition-all duration-300`}
    >
      {featured && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="absolute top-0 left-0 right-0 bg-amber-400 text-amber-900 py-2 text-center text-sm font-bold"
        >
          <Star className="w-4 h-4 inline mr-1" /> MOST POPULAR
        </motion.div>
      )}
      
      <div className={`p-8 ${featured ? 'pt-14' : ''} flex flex-col h-full`}>
        {/* Icon */}
        <motion.div
          whileHover={{ scale: 1.1, rotate: 5 }}
          className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-6 ${
            featured ? 'bg-white/20' : 'bg-[#1E3A8A]/10'
          }`}
        >
          <bundle.icon className={`w-8 h-8 ${featured ? 'text-white' : 'text-[#1E3A8A]'}`} />
        </motion.div>
        
        {/* Title & Price */}
        <h3 className={`text-2xl font-bold mb-2 ${featured ? 'text-white' : 'text-gray-900'}`}>
          {bundle.title}
        </h3>
        <div className={`text-4xl font-bold mb-1 ${featured ? 'text-amber-300' : 'text-[#1E3A8A]'}`}>
          {bundle.price}
        </div>
        <p className={`text-sm mb-4 ${featured ? 'text-blue-200' : 'text-gray-500'}`}>
          one-time payment
        </p>
        
        {/* Tagline */}
        <p className={`text-lg font-medium mb-4 ${featured ? 'text-amber-200' : 'text-gray-700'}`}>
          {bundle.tagline}
        </p>
        
        {/* Description */}
        <p className={`mb-6 leading-relaxed ${featured ? 'text-blue-50' : 'text-gray-600'}`}>
          {bundle.description}
        </p>
        
        {/* Features */}
        <ul className="space-y-3 mb-8 flex-grow">
          {bundle.features.map((feature, idx) => (
            <motion.li
              key={idx}
              initial={{ opacity: 0, x: -10 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ delay: 0.4 + idx * 0.08 }}
              className="flex items-start gap-3"
            >
              <CheckCircle2 className={`w-5 h-5 flex-shrink-0 mt-0.5 ${featured ? 'text-amber-300' : 'text-green-500'}`} />
              <span className={`text-sm ${featured ? 'text-blue-50' : 'text-gray-700'}`}>{feature}</span>
            </motion.li>
          ))}
        </ul>
        
        {/* Turnaround */}
        <div className={`flex items-center gap-2 mb-6 ${featured ? 'text-blue-200' : 'text-gray-500'}`}>
          <Clock className="w-4 h-4" />
          <span className="text-sm">{bundle.turnaround}</span>
        </div>
        
        {/* CTA */}
        <Link to={`/request?project=${encodeURIComponent(bundle.title)}`}>
          <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.98 }}>
            <Button 
              className={`w-full py-6 text-lg font-semibold rounded-xl ${
                featured 
                  ? 'bg-white text-[#1E3A8A] hover:bg-gray-100' 
                  : 'bg-[#1E3A8A] text-white hover:bg-[#1E3A8A]/90'
              }`}
            >
              Get Started <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </motion.div>
        </Link>
      </div>
    </motion.div>
  );
};

// Comparison Feature Row
const ComparisonRow = ({ feature, digital, adBoost, offline, social }) => (
  <tr className="border-b border-gray-100 hover:bg-gray-50/50">
    <td className="py-4 px-4 text-gray-700 font-medium text-sm">{feature}</td>
    <td className="py-4 px-4 text-center">
      {digital ? <CheckCircle2 className="w-5 h-5 text-green-500 mx-auto" /> : <span className="text-gray-300">—</span>}
    </td>
    <td className="py-4 px-4 text-center bg-blue-50/50">
      {adBoost ? <CheckCircle2 className="w-5 h-5 text-green-500 mx-auto" /> : <span className="text-gray-300">—</span>}
    </td>
    <td className="py-4 px-4 text-center">
      {offline ? <CheckCircle2 className="w-5 h-5 text-green-500 mx-auto" /> : <span className="text-gray-300">—</span>}
    </td>
    <td className="py-4 px-4 text-center">
      {social ? <CheckCircle2 className="w-5 h-5 text-green-500 mx-auto" /> : <span className="text-gray-300">—</span>}
    </td>
  </tr>
);

const LaunchBundlesPage = () => {
  usePageMeta({
    title: 'Launch Bundles | BM-Assist.co',
    description: 'Affordable marketing bundles for contractors, lawn care, and home service businesses. Digital setup, ad campaigns, print materials, and social media packages.',
  });

  const bundles = [
    {
      icon: Globe,
      title: "Digital Setup",
      price: "$299",
      tagline: "Get found online",
      description: "Everything you need to show up when customers search for your services. We'll set up and optimize your digital presence from scratch.",
      features: [
        "Google Business Profile setup & optimization",
        "Profile verification assistance",
        "Facebook Business Page creation",
        "Business info & hours configured",
        "First 5 photos uploaded & optimized",
        "Basic SEO keywords added",
        "Review response templates"
      ],
      turnaround: "2-3 business days",
    },
    {
      icon: Megaphone,
      title: "Local Ad Boost",
      price: "$499",
      tagline: "Start getting leads",
      description: "A done-for-you ad campaign to put your business in front of local customers actively searching for your services.",
      features: [
        "2-week Facebook or Google ad campaign",
        "Custom ad creative & copy",
        "Audience targeting setup",
        "Geographic targeting (your service area)",
        "Ad budget management ($100 ad spend included)",
        "Performance tracking dashboard",
        "End-of-campaign report"
      ],
      turnaround: "Campaign starts within 3-5 days",
      featured: true
    },
    {
      icon: Printer,
      title: "Offline Essentials Kit",
      price: "$249",
      tagline: "Look professional everywhere",
      description: "Print-ready materials that make a great first impression. Perfect for job sites, networking, and leave-behinds.",
      features: [
        "Business card design (print-ready files)",
        "Door hanger or flyer design",
        "Consistent branding across materials",
        "High-resolution files (PDF, PNG)",
        "2 rounds of revisions included",
        "Print vendor recommendations"
      ],
      turnaround: "3-5 business days",
    },
    {
      icon: Camera,
      title: "Promo & Social Starter",
      price: "$399",
      tagline: "Content that converts",
      description: "Branded social media content you can use over and over. Stand out on Facebook and Instagram with professional graphics.",
      features: [
        "5 branded social media post templates",
        "Facebook & Instagram cover graphics",
        "Editable Canva templates included",
        "Brand color palette & fonts defined",
        "Before/after post template",
        "Promotional offer template",
        "Quote/testimonial template"
      ],
      turnaround: "4-6 business days",
    }
  ];

  const comparisonFeatures = [
    { feature: "Google Business Profile Setup", digital: true, adBoost: false, offline: false, social: false },
    { feature: "Facebook Page Creation", digital: true, adBoost: false, offline: false, social: false },
    { feature: "SEO Optimization", digital: true, adBoost: false, offline: false, social: false },
    { feature: "Paid Ad Campaign", digital: false, adBoost: true, offline: false, social: false },
    { feature: "Ad Creative & Copywriting", digital: false, adBoost: true, offline: false, social: false },
    { feature: "Ad Spend Included ($100)", digital: false, adBoost: true, offline: false, social: false },
    { feature: "Performance Reporting", digital: false, adBoost: true, offline: false, social: false },
    { feature: "Business Card Design", digital: false, adBoost: false, offline: true, social: false },
    { feature: "Flyer / Door Hanger Design", digital: false, adBoost: false, offline: true, social: false },
    { feature: "Print-Ready Files", digital: false, adBoost: false, offline: true, social: false },
    { feature: "Social Media Templates", digital: false, adBoost: false, offline: false, social: true },
    { feature: "Cover Graphics (FB/IG)", digital: false, adBoost: false, offline: false, social: true },
    { feature: "Editable Canva Files", digital: false, adBoost: false, offline: false, social: true },
    { feature: "Brand Guidelines", digital: false, adBoost: false, offline: false, social: true },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white overflow-x-hidden">
      {/* Navigation */}
      <motion.nav 
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5 }}
        className="fixed top-0 w-full bg-white/90 backdrop-blur-md border-b border-gray-100 z-50"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="text-2xl font-bold bg-gradient-to-r from-[#1E3A8A] to-[#3B82F6] bg-clip-text text-transparent">
              BM-Assist.co
            </Link>
            <Link to="/">
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button variant="outline" className="border-[#1E3A8A] text-[#1E3A8A] hover:bg-[#1E3A8A] hover:text-white rounded-full">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Home
                </Button>
              </motion.div>
            </Link>
          </div>
        </div>
      </motion.nav>

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        {/* Animated background */}
        <motion.div
          className="absolute top-20 right-0 w-96 h-96 bg-blue-200 rounded-full filter blur-3xl opacity-30 -z-10"
          animate={{ x: [0, 30, 0], y: [0, -20, 0] }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute bottom-0 left-0 w-72 h-72 bg-amber-200 rounded-full filter blur-3xl opacity-30 -z-10"
          animate={{ x: [0, -20, 0], y: [0, 20, 0] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        />

        <div className="max-w-4xl mx-auto text-center relative z-10">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            <motion.div
              variants={fadeInUp}
              className="inline-flex items-center gap-2 bg-[#1E3A8A]/10 text-[#1E3A8A] px-4 py-2 rounded-full text-sm font-semibold mb-6"
            >
              <Package className="w-4 h-4" />
              Launch Bundles
            </motion.div>
            
            <motion.h1 
              variants={fadeInUp}
              className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight"
            >
              <span className="text-gray-900">Pick your </span>
              <span className="bg-gradient-to-r from-[#1E3A8A] to-[#3B82F6] bg-clip-text text-transparent">
                launchpad
              </span>
            </motion.h1>
            
            <motion.p 
              variants={fadeInUp}
              className="text-xl text-gray-600 max-w-2xl mx-auto mb-8"
            >
              Focused bundles designed to solve specific problems. 
              Choose what you need now — no subscriptions, no fluff.
            </motion.p>

            {/* Trust badges */}
            <motion.div variants={fadeInUp} className="flex flex-wrap justify-center gap-4 text-sm">
              {[
                { icon: Clock, text: "Fast turnaround" },
                { icon: Shield, text: "100% satisfaction" },
                { icon: Gift, text: "No monthly fees" }
              ].map((item, index) => (
                <motion.div
                  key={item.text}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.5 + index * 0.1 }}
                  className="flex items-center gap-2 bg-white/80 backdrop-blur-sm px-4 py-2 rounded-full shadow-sm border border-gray-100"
                >
                  <item.icon className="w-4 h-4 text-[#1E3A8A]" />
                  <span className="text-gray-700">{item.text}</span>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Bundles Grid */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-6">
            {bundles.map((bundle, index) => (
              <BundleCard key={bundle.title} bundle={bundle} featured={bundle.featured} index={index} />
            ))}
          </div>
          
          {/* Custom option callout */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-8 text-center"
          >
            <p className="text-gray-600">
              Need something different?{' '}
              <Link to="/request" className="text-[#1E3A8A] font-semibold hover:underline">
                Tell us what you need →
              </Link>
            </p>
          </motion.div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Compare <span className="text-[#1E3A8A]">bundles</span>
            </h2>
            <p className="text-lg text-gray-600">
              See exactly what's included in each package
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="overflow-x-auto rounded-xl border border-gray-200"
          >
            <table className="w-full min-w-[700px]">
              <thead>
                <tr className="bg-gray-50 border-b-2 border-gray-200">
                  <th className="py-4 px-4 text-left text-gray-900 font-bold">What's Included</th>
                  <th className="py-4 px-4 text-center">
                    <div className="text-gray-900 font-bold">Digital Setup</div>
                    <div className="text-[#1E3A8A] font-semibold">$299</div>
                  </th>
                  <th className="py-4 px-4 text-center bg-blue-50">
                    <div className="text-gray-900 font-bold">Local Ad Boost</div>
                    <div className="text-[#1E3A8A] font-semibold">$499</div>
                    <div className="text-xs text-amber-600 font-medium">★ Popular</div>
                  </th>
                  <th className="py-4 px-4 text-center">
                    <div className="text-gray-900 font-bold">Offline Kit</div>
                    <div className="text-[#1E3A8A] font-semibold">$249</div>
                  </th>
                  <th className="py-4 px-4 text-center">
                    <div className="text-gray-900 font-bold">Promo & Social</div>
                    <div className="text-[#1E3A8A] font-semibold">$399</div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {comparisonFeatures.map((row, index) => (
                  <ComparisonRow key={index} {...row} />
                ))}
              </tbody>
            </table>
          </motion.div>
          
          {/* Bundle combo suggestion */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-8 bg-gradient-to-r from-amber-50 to-amber-100/50 rounded-2xl p-6 border border-amber-200"
          >
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-amber-400 flex items-center justify-center flex-shrink-0">
                <Zap className="w-5 h-5 text-amber-900" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900 mb-1">Pro Tip: Bundle for Maximum Impact</h3>
                <p className="text-gray-600 text-sm">
                  Most successful clients combine <strong>Digital Setup + Local Ad Boost</strong> to get found online AND start generating leads immediately. 
                  <Link to="/request" className="text-[#1E3A8A] font-semibold hover:underline ml-1">Ask about combo pricing →</Link>
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-4xl mx-auto bg-gradient-to-r from-[#1E3A8A] to-[#3B82F6] rounded-3xl p-8 md:p-12 text-center relative overflow-hidden"
        >
          <motion.div
            className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2"
            animate={{ scale: [1, 1.2, 1] }}
            transition={{ duration: 6, repeat: Infinity }}
          />
          
          <div className="relative z-10">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Not sure which bundle fits?
            </h2>
            <p className="text-xl text-blue-100 mb-8">
              Tell us your biggest challenge and we'll point you in the right direction.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/request">
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Button className="bg-white text-[#1E3A8A] hover:bg-gray-100 text-lg px-8 py-6 rounded-full shadow-xl font-semibold">
                    Start Your Project
                    <Sparkles className="w-5 h-5 ml-2" />
                  </Button>
                </motion.div>
              </Link>
              <Link to="/quick-fixes">
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Button variant="outline" className="border-white text-white hover:bg-white/10 text-lg px-8 py-6 rounded-full font-semibold">
                    Just need one thing?
                  </Button>
                </motion.div>
              </Link>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="mb-4 md:mb-0">
              <span className="text-xl font-bold bg-gradient-to-r from-white to-blue-200 bg-clip-text text-transparent">
                BM-Assist.co
              </span>
            </div>
            <div className="flex gap-6 text-sm text-gray-400">
              <Link to="/quick-fixes" className="hover:text-white transition-colors">Quick Fixes</Link>
              <Link to="/team" className="hover:text-white transition-colors">Our Team</Link>
              <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-500 text-sm">
            © {new Date().getFullYear()} BM-Assist.co. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LaunchBundlesPage;
